<template>
  <div class="profileBanner" data-test-id="influencerProfileBanner">
    <ImageCropper
      ref="cropper"
      target="banner"
      :aspect-ratio-x="128"
      :aspect-ratio-y="29"
      :modal-title="$t('influencer.profile.slug.edit.picture01')"
    >
      <template #actions>
        <div class="bottomActionWrap">
          <div class="change" @click="addPicture">
            {{ $t('shared.profile.banner00') }}
          </div>
          <ActionButton @click="confirmCrop">
            <div class="btnText">
              {{ $t('shared.profile.banner01') }}
            </div>
          </ActionButton>
        </div>
      </template>
    </ImageCropper>
    <transition>
      <div v-if="influencer.has_banner || picture" class="imageHolder">
        <div class="filter" />
        <img class="image" :src="bannerUrl" alt="" />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'

import ImageCropper from '~/components/images/cropper.vue'
import ActionButton from '~/components/shared/ActionButton.vue'

import { useGetServerSyncedTimestamp } from '~/composables/useGetServerSyncedTimestamp'

import { useUserStore } from '~/stores/user'
import { useUserInfluencerStore } from '~/stores/userInfluencer'

import pictureBuild from '~/helpers/images/picture_build'

import type { Influencer } from '~/types/influencer'

type Props = {
  influencer: Influencer
  canEdit?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  canEdit: false,
})

const { id: USER_ID } = storeToRefs(useUserStore())
const { SET: SET_INFLUENCER } = useUserInfluencerStore()

const picture = ref<string | undefined>()
const cropper = ref<InstanceType<typeof ImageCropper> | null>(null)
const { timestamp } = useGetServerSyncedTimestamp('banner-image')

const bannerUrl = computed(() => {
  if (picture.value) {
    return picture.value
  } else {
    return (
      pictureBuild({
        kind: 'influencer',
        slug: props.influencer.slug,
        target: 'banner',
        size: '1280_290',
      }) + `?ts=${timestamp.value}`
    )
  }
})

defineExpose({
  addPicture,
  confirmCrop,
})

function addPicture() {
  cropper.value?.addPicture()
}

function confirmCrop() {
  if (!cropper.value) return
  picture.value = cropper.value.getCroppedCanvas()
  cropper.value.close()
  cropper.value
    .upload({
      id: USER_ID.value,
      slug: props.influencer.slug,
      kind: 'influencer',
    })
    .then((resp: { picture: string }) => {
      picture.value = resp.picture
      SET_INFLUENCER({ has_banner: true })
    })
    .catch((err: any) => {
      console.error(err)
    })
}
</script>

<style scoped>
/* TODO Check which classes are still relevant during testing.*/
@media screen and (max-width: 720px) {
  .editPrompt {
    max-width: 75px;
  }

  .editPrompt > div {
    padding: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 75px;
    height: 75px;
  }
}

.title {
  font-size: var(--font-size-title);
  font-weight: 700;
  
  margin-bottom: 8px;
}

.profileBanner {
  width: 100%;
}

.profileBanner img {
  width: 100%;
  height: 100%;
  object-position: center;
  object-fit: cover;
}

.editPrompt {
  position: absolute;
  right: 24px;
  bottom: 24px;
  display: flex;
  justify-content: flex-end;
  z-index: 3;
  opacity: 0;
  max-width: 40px;
  transition: opacity 0.15s var(--trans-standard);
  color: var(--color-white);
  cursor: pointer;
}

.profileBanner:hover .editPrompt {
  opacity: 1;
  transition: opacity 0.1s var(--trans-decelerate);
}

.bottomActionWrap {
  color: var(--color-main);
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 24px auto;
}

.bottomActionWrap > .change {
  cursor: pointer;
  margin-right: 16px;
}

.favoritePrompt {
  top: 22px;
  right: 44px;
  position: absolute;
  cursor: pointer;
  z-index: 2;
  width: 40px;
  height: 40px;
  border-radius: 40px;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  background-color: var(--color-background);
  color: var(--color-black);
}

.imageHolder {
  height: 100%;
}

.filter {
  position: absolute;
  background: rgba(0, 0, 0, 0.75);
  width: 100%;
  height: 100%;
}
</style>
